import { Subtitles, MusicCueSheet } from "models";
import { ContentType, SegregationType } from "commons/form-constants";

export enum Currency {
	USD = "USD", EUR = "EUR"
}

export interface PayPerViewSegregationAdditionalInfos {
	price: number;
	currency: Currency;
	stripeProductId?: any;
}

export enum OriginalFormat {
	MM35 = "MM35",
	HD = "HD"
}

export enum Framing {
	FULL = "FULL",
	PAN_AND_SCAN = "PAN_AND_SCAN",
	LB_235 = "LB_235",
	LB_185 = "LB_185",
	LB_16X9 = "LB_16X9"
}

export enum NativeFrameRate {
	FPS_25 = "FPS_25",
	FPS_24 = "FPS_24",
	FPS_23_976 = "FPS_23_976" //23.976 fps
}

export enum VODSource {
	NTSC = "NTSC",
	PAL = "PAL"
}

export enum VODSound {
	DOLBY_SR = "DOLBY_SR",
	DOLBY_DIGITAL = "DOLBY_DIGITAL",
	DOLBY_PRO_LOGIC = "DOLBY_PRO_LOGIC",
	STEREO = "STEREO"
}
export interface VodAdditionalInfos {
	fileName?: string;
	format?: string;
	durationInSeconds?: number;
	vodS3Key?: string;
	needsEncoding?: boolean;
	encodedContentS3Key?: string;
	encodingJobExecutionId?: string | null;
	originalFormat?: OriginalFormat;
	framing?: Framing;
	nativeFrameRate?: NativeFrameRate;
	source?: VODSource;
	sound?: VODSound;
	closedCaptioned?: boolean;
	masterDeliveryFormat?: VODSource;
}

export interface LiveAdditionalInfos {
	eventStartDateTime: Date;
	eventEndDateTime: Date;
	streamingInputRTMPUrls: string[] | null,
	contentOutputUrl: string | null,
	resourceCreationStackName: string | null,
	resourceCreationSFExecutionArn: string | null,
	resourceDeletionSFExecutionArn: string | null,
	releaseSFExecutionArn: string | null,
	mediaLiveChannelArn: string | null
}

export interface ThumbnailData {
	thumbnailS3Key?: string;
	fileName: string;
	format: string;
}

export enum ContentStatus {
	PENDING_LIVE_RESOURCES_CREATION = "PENDING_LIVE_RESOURCES_CREATION",
	PENDING_LIVE_RESOURCES_START = "PENDING_LIVE_RESOURCES_START",
	ERROR = "ERROR",
	PENDING_UPLOAD = "PENDING_UPLOAD",
	PENDING_THUMB_UPLOAD = "PENDING_THUMB_UPLOAD",
	PENDING_ENCODING = "PENDING_ENCODING",
	READY = "READY",
	LIVE_NOW = "LIVE_NOW",
	ENDED_LIVE = "ENDED_LIVE",
	PENDING_APPROVAL = "PENDING_APPROVAL",
	NOT_APPROVED = "NOT_APPROVED"
}

export interface ContentDescription {
	language: string;
	description: string;
}

export enum AgeClassification {
	ALL = "ALL",
	OLDER_7 = "OLDER_7",
	OLDER_10 = "OLDER_10",
	OLDER_13 = "OLDER_13",
	OLDER_16 = "OLDER_16",
	OLDER_18 = "OLDER_18",
}

export enum ContentSubmitionType {
	BACKOFFICE = "BACKOFFICE",
	CSV = "CSV"
}

export interface ContentAdditionalInfos {
	dateSubmission?: Date;
	marketingOpportunities?: string[];
	musicAgreements?: string;
	csvSubtitles?: string;
	onlineExposure?: boolean;
	exposureType?: string;
	accessType?: string;
	filmMood?: string;
	contentSubmitionType?: ContentSubmitionType;
	minorityLanguage?: string;
}

export enum RightsOwnerType {
	COMPANY = "COMPANY",
	INDIVIDUAL = "INDIVIDUAL"
}

export interface RightsOwner {
	type?: RightsOwnerType;
	companyName?: string;
	legalRepresentative?: string;
	streetAddress?: string;
	city?: string;
	state?: string;
	postcode?: string;
	country?: string;
	email?: string;
	phoneNumber?: string;
	website?: string;
	directorFirstname?: string;
	directorSurname?: string;
	directorBirthDate?: Date;
	directorBirthPlace?: string;
	directorAddress?: string;
	directorPostalCode?: string;
	directorCity?: string;
	directorState?: string;
	directorCountry?: string;
	directorPhone?: string;
	directorPhoneMobile?: string;
	directorEmail?: string;
	directorWebsite?: string;
	directorRole?: string;
	directorGender?: string;
	directorEthnicity?: string;
}

export interface SeriesData {
	seriesId: string;
	episodeNumber: number;
	season: number;
}

export interface Content {
	id: string;
	type: ContentType;
	title: ContentDescription[];
	synopsis: ContentDescription[];
	dialogue: boolean;
	language: string;
	genre: string[];
	director: string[];
	producer: string[];
	productionDirector: string[];
	cast: string[];
	writer: string[];
	composer: string[];
	dop: string[];
	editor: string[];
	festivals: string[];
	awards: string[];
	country: string;
	seriesData?: SeriesData;
	productionYear: string;
	music: boolean;
	musicCueSheet: MusicCueSheet[]
	color: boolean;
	subtitles: Subtitles[];
	ageClassification: AgeClassification;
	contentIssues: string[];
	academyQualified: string[];
	segregationType: SegregationType;
	creationDate: Date;
	releaseDate: Date;
	released: boolean;
	status: ContentStatus;
	isHighlighted: boolean;
	payPerViewSegregationAdditionalInfos: PayPerViewSegregationAdditionalInfos | null;
	vodAdditionalInfos: VodAdditionalInfos | null;
	liveAdditionalInfos: LiveAdditionalInfos | null;
	geoBlockedCountryCodes?: string[] | null;
	tagList: string[];
	thumbnailData: ThumbnailData;
	contentServingUrl: string;
	thumbnailServingUrl: string;
	isReleaseDatePublic: boolean;
	durationInSeconds: number;
	rightsOwner: RightsOwner;
	contentAdditionalInfos: ContentAdditionalInfos;
}

export interface ContentWithUserDetails extends Content {
	isUserFavorite: boolean;
	isUserLiked: boolean;
	lastVisionTimestamp: number;
}